import React, { useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Home from './components/Home';
import ProfilePage from './components/ProfilePage';
import Login from './components/Login';
import Layout from './components/Layout';
import './index.css';

// Ordre des pages pour déterminer la direction
const pageOrder = {
    '/': 1,
    '/profile': 2
};

// Variants avec custom pour direction dynamique
const pageVariants = {
    enter: (direction) => ({
        x: direction > 0 ? '100%' : '-100%',
    }),
    center: {
        x: 0,
    },
    exit: (direction) => ({
        x: direction > 0 ? '-100%' : '100%',
    }),
};

// Composant qui gère les routes animées
const AnimatedRoutes = ({ isAuthenticated, onLogin, onLogout }) => {
    const location = useLocation();
    const prevPathRef = useRef(location.pathname);
    const [direction, setDirection] = useState(0);

    // Calculer la direction quand le path change
    React.useEffect(() => {
        const currentOrder = pageOrder[location.pathname] ?? 1;
        const prevOrder = pageOrder[prevPathRef.current] ?? 1;

        if (location.pathname !== prevPathRef.current) {
            setDirection(currentOrder > prevOrder ? 1 : -1);
            prevPathRef.current = location.pathname;
        }
    }, [location.pathname]);

    // Si pas authentifié, afficher login sans layout
    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="*" element={<Login onLogin={onLogin} />} />
            </Routes>
        );
    }

    // Authentifié: afficher avec layout et animations
    return (
        <Layout onLogout={onLogout}>
            <AnimatePresence initial={false} mode="popLayout" custom={direction}>
                <motion.div
                    key={location.pathname}
                    custom={direction}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        mass: 1
                    }}
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        overflow: 'auto',
                        backgroundColor: '#f9fafb'
                    }}
                >
                    <Routes location={location}>
                        <Route path="/" element={<Home />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </motion.div>
            </AnimatePresence>
        </Layout>
    );
};

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const handleLogin = () => {
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
    };

    return (
        <Router>
            <AnimatedRoutes
                isAuthenticated={isAuthenticated}
                onLogin={handleLogin}
                onLogout={handleLogout}
            />
        </Router>
    );
}

export default App;
